import React, { useEffect, useMemo, useState } from 'react';
import { X, CheckCircle2, AlertCircle, Info, ArrowRight, Loader2 } from 'lucide-react';
import { runAudit } from '../../utils/audit';

/**
 * Props:
 * - isOpen, onClose
 * - product: { id, productTitle, auditOverrides? }
 * - audit: initial audit object (optional)
 * - onQuickFix(key, product)
 * - onRerunAudit(): Promise<void>
 * - onUpdateAuditOverrides(productId, completeOverridesObject): Promise<void>
 * - onSyncActiveProduct?(productId): Promise<void>
 * - onOpenCoachPanel?(product): void   // <-- CORRECTION: Now expects the full product object
 */
export default function ListingAuditDrawer({
  isOpen,
  onClose,
  product,
  audit,
  onQuickFix,
  onRerunAudit,
  onUpdateAuditOverrides,
  onSyncActiveProduct,
  onOpenCoachPanel,
}) {
  // Local draft of overrides – this is what the UI reflects immediately.
  const [draftOverrides, setDraftOverrides] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [openGuidanceKeys, setOpenGuidanceKeys] = useState({});

  const isEqualBoolMap = (a = {}, b = {}) => {
    const ak = Object.keys(a);
    const bk = Object.keys(b);
    if (ak.length !== bk.length) return false;
    for (const k of ak) {
      if (!!a[k] !== !!b[k]) return false;
    }
    return true;
  };

  const serverOverrides = product?.auditOverrides || {};
  const [isDirty, setIsDirty] = useState(false);

  // Keep draft in sync with server when not dirty/saving.
  useEffect(() => {
    if (!product?.id) return;
    if (!isDirty && !isSaving) {
      setDraftOverrides(serverOverrides);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id, serverOverrides]);

  useEffect(() => {
    setIsDirty(!isEqualBoolMap(draftOverrides, serverOverrides));
  }, [draftOverrides, serverOverrides]);

  const title = useMemo(
    () => product?.productTitle || product?.title || 'Untitled Product',
    [product]
  );

  // Local recompute so score updates instantly while editing draft overrides.
  const viewAudit = useMemo(() => {
    if (!product) return null;
    try {
      return runAudit(product, draftOverrides);
    } catch (e) {
      console.warn('runAudit failed in drawer; falling back to prop audit.', e);
      return audit || null;
    }
  }, [product, draftOverrides, audit]);

  const score = viewAudit?.score ?? 0;
  const ringStyle = {
    backgroundImage: `conic-gradient(#0ea5e9 ${score * 3.6}deg, #e5e7eb 0deg)`,
  };

  const toggleGuidance = (key) =>
    setOpenGuidanceKeys((s) => ({ ...s, [key]: !s[key] }));

  const handleStartRewrite = () => {
    onQuickFix?.('intro', product);
    onClose?.();
  };

  // Local-only toggle
  const handleDraftToggle = (key, checked) => {
    setDraftOverrides((prev) => {
      const next = { ...prev };
      if (checked) next[key] = true;
      else delete next[key];
      return next;
    });
  };

  const handleResetDraft = () => {
    setDraftOverrides(serverOverrides);
    // Also pull newest product/audit from host
    Promise.resolve()
      .then(async () => {
        if (typeof onSyncActiveProduct === 'function' && product?.id) {
          await onSyncActiveProduct(product.id);
        }
        await onRerunAudit?.();
      })
      .catch(() => {});
  };

  const handleSaveDraft = async () => {
    if (!product?.id || !onUpdateAuditOverrides) return;
    setIsSaving(true);
    try {
      await onUpdateAuditOverrides(product.id, draftOverrides);

      // Pull freshest product, then re-run so Coach/Hub & pop-out stay aligned
      if (typeof onSyncActiveProduct === 'function') {
        await onSyncActiveProduct(product.id);
      }
      await onRerunAudit?.();
    } catch (e) {
      console.error('Failed to save overrides:', e);
      alert('Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const smallButton =
    'inline-flex items-center px-3 py-1.5 rounded-md text-[12px] font-medium disabled:opacity-60 disabled:cursor-not-allowed';
  const subtleButton =
    `${smallButton} border border-slate-300 text-slate-700 hover:bg-slate-50`;
  const primaryButton =
    `${smallButton} bg-sky-600 hover:bg-sky-700 text-white shadow`;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60]">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/45" onClick={onClose} />

      {/* Drawer */}
      <aside className="absolute right-0 top-0 h-full w-full sm:w-[680px] bg-white shadow-2xl border-l flex flex-col">
        {/* Header */}
        <header className="px-4 py-4 bg-gradient-to-r from-sky-400 to-blue-500 text-white flex items-start justify-between">
          <div className="pr-4">
            <div className="text-[11px] uppercase tracking-wider/loose opacity-90">Listing Audit</div>
            <h3 className="text-base font-semibold leading-tight mt-0.5 line-clamp-2">{title}</h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/30"
            aria-label="Close audit"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        {/* Body */}
        <div className="relative flex-1 overflow-y-auto">
          <div className="p-4 sm:p-5">
            {/* Discreet banner */}
            <div className="flex items-start gap-2 rounded-lg border border-sky-100 bg-sky-50/60 px-3 py-2 mb-4">
              <Info className="w-4 h-4 mt-0.5 text-sky-600" />
              <p className="text-[12px] text-slate-700 leading-snug">
                Made changes in the Coach Panel? Click <span className="font-semibold">Reset</span> below to pull the latest here.
                Remember to click <span className="font-semibold">Save Not Relevant</span> to persist changes you make in this panel.
              </p>
            </div>

            {!viewAudit ? (
              <div className="text-sm text-slate-500">Loading audit results…</div>
            ) : (
              <>
                {/* Score Card */}
                <div className="flex items-center gap-4 p-4 rounded-xl border bg-slate-50">
                  <div
                    className="shrink-0 w-20 h-20 rounded-full grid place-items-center"
                    style={ringStyle}
                    aria-label={`Score ${score}`}
                  >
                    <div className="w-16 h-16 rounded-full bg-white grid place-items-center text-sky-700 font-extrabold text-2xl">
                      {score}
                    </div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-wider text-slate-500">Overall Score</div>
                    <div className="text-slate-800 font-medium mt-0.5 text-sm">{viewAudit.summary}</div>
                    <div className="text-[12px] text-slate-500 mt-2">
                      Words: <strong>{viewAudit.breakdown.wordCount}</strong>
                      <span className="mx-2 text-slate-300">•</span>
                      Avg sentence: <strong>{viewAudit.breakdown.avgSentenceLength}</strong>
                    </div>
                  </div>
                </div>

                {/* Checks (based on DRAFT overrides) */}
                <section className="mt-5 space-y-3">
                  {(viewAudit.items || [])
                    .slice()
                    .sort((a, b) => Number(a.pass) - Number(b.pass))
                    .map((c) => {
                      const passing = !!c.pass;
                      const isOverridden = !!draftOverrides[c.key];
                      const open = !!openGuidanceKeys[c.key];

                      return (
                        <div
                          key={c.key}
                          className={`rounded-lg border p-3 transition-colors ${
                            passing || isOverridden
                              ? 'bg-white border-slate-200'
                              : 'bg-amber-50/60 border-amber-200'
                          } ${isOverridden ? 'opacity-70' : ''}`}
                        >
                          <div className="flex items-start gap-3">
                            {passing || isOverridden ? (
                              <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                            ) : (
                              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="font-medium text-slate-800 text-sm">{c.label}</div>

                              {!passing && !isOverridden && (
                                <div className="text-[12px] text-slate-600 mt-0.5">{c.hint}</div>
                              )}
                              {isOverridden && (
                                <div className="text-[12px] text-slate-500 mt-0.5 italic">
                                  (Override: Not Relevant)
                                </div>
                              )}

                              {Array.isArray(c.guide) && c.guide.length > 0 && (
                                <div className="mt-2">
                                  <button
                                    onClick={() => setOpenGuidanceKeys(s => ({ ...s, [c.key]: !s[c.key] }))}
                                    className="text-[12px] font-medium text-sky-700 hover:text-sky-800"
                                  >
                                    {open ? 'Hide guidance' : 'More guidance'}
                                  </button>
                                  {open && (
                                    <ul className="mt-1 list-disc list-inside text-[12px] text-slate-600 space-y-1">
                                      {c.guide.map((g, i) => (
                                        <li key={i}>{g}</li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* NOT RELEVANT CHECKBOX (local only until Save) */}
                            <label
                              className={`flex items-center gap-1 text-[12px] font-medium text-slate-600 shrink-0 ${
                                isSaving ? 'cursor-wait opacity-70' : 'cursor-pointer'
                              }`}
                              title="Mark this audit item as not relevant for this product."
                            >
                              <input
                                type="checkbox"
                                className="rounded border-slate-300 disabled:opacity-50 disabled:cursor-wait"
                                checked={isOverridden}
                                onChange={(e) => handleDraftToggle(c.key, e.target.checked)}
                                disabled={isSaving}
                              />
                              {'Not relevant'}
                            </label>
                          </div>
                        </div>
                      );
                    })}
                </section>
              </>
            )}
          </div>
        </div>

        {/* Save Bar */}
        <footer className="border-t bg-white px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="text-[12px] text-slate-500 truncate">
              {isDirty
                ? 'You have unsaved “Not relevant” changes.'
                : 'Apply improvements in the rewrite tool.'}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handleResetDraft}
                className={subtleButton}
                disabled={isSaving}
                title="Reset to latest and pull fresh data from Coach Panel"
              >
                Reset
              </button>

              {isDirty && (
                <button
                  onClick={handleSaveDraft}
                  className={primaryButton}
                  title="Save Not Relevant"
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…
                    </>
                  ) : (
                    'Save Not Relevant'
                  )}
                </button>
              )}

              {/* Coach Panel button – closes drawer before opening panel */}
              <button
                // FIX: Pass the full 'product' object, not just 'product?.id', as expected by App.js
                onClick={() => { onClose?.(); onOpenCoachPanel?.(product); }}
                className={primaryButton}
                title="Coach Panel"
                disabled={isSaving || !product?.id}
              >
                Coach Panel
                <ArrowRight className="w-4 h-4 ml-2 -mr-1" />
              </button>

              <button
                onClick={onClose}
                className={subtleButton}
                disabled={isSaving}
              >
                Close
              </button>
            </div>
          </div>
        </footer>
      </aside>
    </div>
  );
}